import type { UploadRules, UploadType } from './uploadQueue';
import i18n from '../../i18n';

/**
 * Supervision's configuration of the upload primitive: the review Documents tab, the only upload
 * surface in this app (`POST client-reviews/{reviewId}/documents`, DocumentFunctions.UploadReviewDocument).
 *
 * The rules MIRROR the server, and `serverLimits.test.ts` reads DocumentFunctions.cs to keep them
 * equal: a browser list narrower than the server's refuses files the server would take, and a wider
 * one sends files the server will refuse after the upload has already been paid for.
 */
/**
 * NO LIMIT: every dropped file starts at once (Christian, 2026-10-27, "unlimited after one more clean day").
 *
 * The clean day, measured read-only on 2026-10-28: 53 uploads since deploy #44 at concurrency 2, all 200, no 409,
 * 35 of them a second file on the same review within 2 s (real multi-file drops, so the race was exercised), and
 * the lost-record audit still at the 12 held from before #213. ptc-docs crm/PROGRESS_LOG.md, 2026-10-28.
 *
 * If a lost record ever reappears, set this back to 1 at once: the history below is why.
 *
 * Before that: two at a time, the primitive's default, restored 2026-10-26.
 *
 * It was 1 from 2026-10-21 (#210) as an interim fix. The upload handler used to replace the whole review
 * with no ETag, so two concurrent uploads raced and the later write dropped the earlier one's record while
 * both answered 200. Elvia lost a Balance Sheet that way in 3 of 3 drops on Cortes FL Carpentry; 18 records
 * were lost since 2026-08-12.
 *
 * The real fix shipped in #213 (deploy #39): an optimistic write (ETag, re-apply on 412) plus a read-back
 * per file. Christian's condition for raising this was "only if the lost-record check passed on real
 * uploads since Thursday". It passed: 88 uploads, 0 lost, 0 conflicts (ptc-docs
 * crm/evidence/2026-10-26-supervision-dp-upload-1-reattach.md). ⚠ Those 88 ran one at a time, so traffic
 * has not yet exercised the race itself; the 412 path is proven by tests. Re-run the lost-record check
 * after the first real multi-file drops at 2.
 */
export const REVIEW_UPLOAD_CONCURRENCY = Number.POSITIVE_INFINITY;

export const REVIEW_DOCUMENT_RULES: UploadRules = {
  extensions: ['pdf', 'xlsx', 'xls', 'doc', 'docx', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'txt'],
  maxBytes: 25 * 1024 * 1024,
};

export const REVIEW_DOCUMENT_TYPES: UploadType[] = [
  {
    value: 'profit-loss',
    // A getter, so the label reads in the UI language at the moment it is shown.
    get label() {
      return i18n.t('documentType.profit-loss');
    },
    match: [/profit\s*(and|&)?\s*loss/i, /\bp\s*(&|and)\s*l\b/i, /\bpnl\b/i, /\bp-?l\b/i, /income\s*statement/i],
  },
  {
    value: 'balance-sheet',
    get label() {
      return i18n.t('documentType.balance-sheet');
    },
    match: [/balance\s*sheet/i, /\bbs\b/i],
  },
  {
    // Xero's "Account Transactions" report IS the general-ledger detail. The checklist requires it
    // ("Account Transactions / General Ledger delivered"), but there was no type for it, so reviewers
    // filed 182 of them as "Trial Balance", the nearest of three (audit, 2026-10-21).
    value: 'general-ledger',
    get label() {
      return i18n.t('documentType.general-ledger');
    },
    match: [/account\s*transactions?/i, /general\s*ledger/i, /\bgl\b/i, /\bgld\b/i],
  },
  {
    value: 'trial-balance',
    get label() {
      return i18n.t('documentType.trial-balance');
    },
    match: [/trial\s*balance/i, /\btb\b/i],
  },
];

/** A stored document type in the UI language; an unknown one is shown as stored, hyphen spaced. */
export function documentTypeLabel(value: string): string {
  return REVIEW_DOCUMENT_TYPES.find((t) => t.value === value)?.label ?? value.replace('-', ' ');
}

